import { useSearch } from '@tanstack/react-router';
import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthStore, type AvailableTenant } from '~/store/auth.store';

/** Stable fallback: a fresh `[]` from the selector would re-render the subscriber forever. */
const NO_TENANTS: AvailableTenant[] = [];

/** The section-wide selected subsidiary (id + display name). */
export interface SubsidiaryScope {
  id: string;
  name: string;
}

/** `SubsidiaryScope` plus whether the operator actually picked it, which callers need in order
 *  to decide if `?sc=` should be forwarded onto links. */
export interface ResolvedSubsidiaryScope extends SubsidiaryScope {
  /**
   * True when `?sc=` named a subsidiary the operator can reach — i.e. they chose it. Links must
   * forward the param only in that case, or every URL gains a redundant `?sc=<own-tenant>`.
   */
  hasExplicitScope: boolean;
}

/** Shared subsidiary scope, provided by a layout and consumed by nested pages. */
export const SubsidiaryScopeContext = createContext<SubsidiaryScope | null>(null);

/** Reads the shared subsidiary scope. Throws if used outside a provider — either
 *  `SubsidiaryScopedTabsLayout` (Integrations / Explorer) or the standalone
 *  upload-delivery layout. */
export function useSubsidiaryScope(): SubsidiaryScope {
  const ctx = useContext(SubsidiaryScopeContext);
  if (!ctx) {
    throw new Error('useSubsidiaryScope must be used within a SubsidiaryScopeContext provider');
  }
  return ctx;
}

/**
 * Resolves the scope a layout should provide: the shared `?sc=` param when it names a
 * subsidiary the operator can actually reach, otherwise their active tenant. Validating
 * against `accessibleTenants` is what stops a shared link from scoping someone into a
 * subsidiary they have no access to.
 *
 * Lives here so every subsidiary-scoped shell (Integrations, Explorer, the standalone
 * upload-delivery layout) resolves the scope identically — `?sc=` has to mean the same
 * thing on both sides of a navigation boundary, or a flow would silently change
 * subsidiary when it is opened from another section.
 */
export function useResolvedSubsidiaryScope(): ResolvedSubsidiaryScope {
  const { t } = useTranslation('common');
  const activeTenantId = useAuthStore((s) => s.user?.activeTenantId ?? '');
  const accessibleTenants = useAuthStore((s) => s.user?.accessibleTenants ?? NO_TENANTS);
  const search = useSearch({ strict: false });

  const scParam = typeof search.sc === 'string' ? search.sc : undefined;
  const hasExplicitScope =
    scParam !== undefined && accessibleTenants.some((tn) => tn.id === scParam);
  const id = hasExplicitScope ? scParam : activeTenantId;
  const name =
    accessibleTenants.find((tn) => tn.id === id)?.name ?? t('subsidiaryScope.currentSubsidiary');

  return { id, name, hasExplicitScope };
}
